
import { isTelegramShareCancelled } from '../lib/shareErrors';

type SdkModule = typeof import('@telegram-apps/sdk-react');

const INVOICE_STATUSES = ['paid', 'cancelled', 'failed', 'pending'] as const;

let sdkPromise: Promise<SdkModule | null> | null = null;
let initPromise: Promise<void> | null = null;

function loadSdk(): Promise<SdkModule | null> {
  if (sdkPromise) return sdkPromise;
  if (typeof window === 'undefined') {
    sdkPromise = Promise.resolve(null);
    return sdkPromise;
  }
  sdkPromise = import('@telegram-apps/sdk-react');
  return sdkPromise;
}

// WHY: SDK isAvailable() is false even inside Telegram until init() registered the bridge — TmaApp's init effect can race
// a user tap
async function ensureSdk(): Promise<SdkModule | null> {
  const sdk = await loadSdk();
  if (!sdk) return null;
  if (!initPromise) {
    initPromise = (async () => {
      try {
        sdk.init();
      } catch (err) {
        console.error('[tmaActions] init failed', err);
      }
    })();
  }
  await initPromise;
  return sdk;
}

export type {
  ShowPopupOptions,
  ShowPopupOptionsButton,
} from '@telegram-apps/sdk-react';

export const tmaActions = {
  async openLink(url: string, options?: { tryInstantView?: boolean }): Promise<void> {
    const sdk = await loadSdk();
    if (sdk && sdk.openLink.isAvailable()) {
      sdk.openLink(url, options);
      return;
    }
    if (typeof window !== 'undefined') {
      window.open(url, '_blank');
    }
  },

  async close(): Promise<void> {
    const sdk = await loadSdk();
    if (sdk && sdk.closeMiniApp.isAvailable()) {
      sdk.closeMiniApp();
    }
  },

  async platform(): Promise<string | null> {
    const sdk = await loadSdk();
    if (!sdk) return null;
    try {
      return sdk.retrieveLaunchParams().tgWebAppPlatform;
    } catch {
      return null;
    }
  },

  async openTelegramLink(url: string): Promise<void> {
    const sdk = await loadSdk();
    if (sdk && sdk.openTelegramLink.isAvailable()) {
      sdk.openTelegramLink(url);
      return;
    }
    if (typeof window !== 'undefined') {
      window.open(url, '_blank');
    }
  },

  async shareMessage(msgId: string | number, callback?: (status: 'sent' | 'cancelled') => void): Promise<void> {
    const sdk = await loadSdk();
    if (!sdk || !sdk.shareMessage.isAvailable()) {
      callback?.('cancelled');
      return;
    }
    try {
      await sdk.shareMessage(String(msgId));
      callback?.('sent');
    } catch (err: unknown) {
      if (isTelegramShareCancelled(err)) {
        callback?.('cancelled');
        return;
      }
      throw err;
    }
  },

  async shareURL(url: string, text?: string): Promise<void> {
    const sdk = await loadSdk();
    if (sdk && sdk.shareURL.isAvailable()) {
      sdk.shareURL(url, text);
    }
  },

  invoice: {
    async isAvailable(): Promise<boolean> {
      const sdk = await ensureSdk();
      return !!sdk && sdk.invoice.open.isAvailable();
    },
    async open(slugOrUrl: string, type?: 'url'): Promise<'paid' | 'cancelled' | 'failed' | 'pending'> {
      const sdk = await ensureSdk();
      if (!sdk || !sdk.invoice.open.isAvailable()) {
        return 'cancelled';
      }
      const status =
        type === 'url'
          ? await sdk.invoice.open(slugOrUrl.replace('://telegram.me/', '://t.me/'), 'url')
          : await sdk.invoice.open(slugOrUrl);
      return INVOICE_STATUSES.find((known) => known === status) ?? 'cancelled';
    },
  },

  async isShareMessageAvailable(): Promise<boolean> {
    const sdk = await loadSdk();
    return !!sdk && sdk.shareMessage.isAvailable();
  },
};

export const popup = {
  async open(
    options: import('@telegram-apps/sdk-react').ShowPopupOptions,
  ): Promise<string | null | undefined> {
    const sdk = await loadSdk();
    if (!sdk || !sdk.popup.show.isAvailable()) return undefined;
    return await sdk.popup.show(options);
  },
};
